import { mapGetCartItemsLineToProduct, type Product } from "@jti/magento";
import type { MagentoApiGetCartItemsQueryResponse } from "@jti/magento-api-v2";
import { getUser, LoyaltyApi } from "@jti/rest-api";

import { TAGS } from "../common/tags";
import type { MagentoApiBuilder } from "../magentoApiSlice";

export type AvailableFreeGiftV2 = {
  sales_rule_id: number;
  gift_qty_limit: number;
  is_removable: boolean;
  allowed_gift_products: { id: number; sku: string; name: string }[];
};

export type GetCartItemsResult = {
  items: Product[];
  availableFreeGifts: AvailableFreeGiftV2[];
};

export type GetCartItemsQueryArg = {
  cartId: string;
};

export const getCartItemsEndpoint = (builder: MagentoApiBuilder) => ({
  getCartItems: builder.query<GetCartItemsResult, GetCartItemsQueryArg>({
    query: ({ cartId }) => ({
      method: "getCartItems",
      params: [{ cartId }],
    }),
    transformResponse: async (response: MagentoApiGetCartItemsQueryResponse, _meta): Promise<GetCartItemsResult> => {
      const isLoyaltyActive = await LoyaltyApi.init().getStatus();

      const validLines = (response?.cart?.items ?? []).filter(Boolean);

      const availableFreeGifts: AvailableFreeGiftV2[] = (response?.cart?.available_free_gifts ?? [])
        .filter(Boolean)
        .map((gift) => ({
          sales_rule_id: gift!.sales_rule_id,
          gift_qty_limit: gift!.gift_qty_limit,
          is_removable: gift!.is_removable,
          allowed_gift_products: (gift!.allowed_gift_products ?? []).filter(Boolean).map((p) => ({
            id: p!.id,
            sku: p!.sku,
            name: p!.name,
          })),
        }));

      if (!isLoyaltyActive || validLines.length === 0) {
        return {
          items: validLines.map((line) => mapGetCartItemsLineToProduct(line)),
          availableFreeGifts,
        };
      }

      const user = getUser();

      const items = await Promise.all(
        validLines.map((line: unknown) => {
          const productLine = line as { product: { sku: string }; quantity: number };

          return LoyaltyApi.init()
            .getProductPoints(productLine.product.sku, productLine.quantity, user?.uuid, user?.ciam_token)
            .then(({ points, rules }) => mapGetCartItemsLineToProduct(productLine, points, rules))
            .catch(() => mapGetCartItemsLineToProduct(productLine));
        })
      );

      return { items, availableFreeGifts };
    },
    providesTags: [TAGS.CART_ITEMS],
  }),
});
